// Email-mode row component + its hover-toolbar + click-to-add "+" (see DND_EMAIL_MODE_SPEC.md §5).
//
// Each pre-composed row (Blocks/Email/blocks.js) is a `<table data-gjs-type="email-row">`. This makes
// it a selectable `email-row` component that shows a hover-toolbar: add-below · move up · move down ·
// duplicate · delete — so a basic user builds the email with BUTTONS, not drag. Rows are also
// `draggable: false`, so the only way to reorder them is the toolbar (can't-break-it). The inner
// tbody/tr/td stay locked; row content stays edit-in-place.
//
// Click-to-add ("+", slice 3b): a picker built from the shared EMAIL_ROWS catalogue lets the user
// insert a complete row at a position with a click — no drag from the palette. Entry points:
//   • each row's "+" toolbar button → inserts a row directly BELOW that row;
//   • a persistent "+ Add content" panel button (config/panels.js, email mode) → appends to the end.

import { applyOutlookSafePageWidth } from 'helpers';
import { buildEmailRows } from './blocks';
import { buildEmailSections } from './sections';

const EMAIL_ROW = 'email-row';

// The curated row toolbar (add-below · up · down · duplicate · delete). Shared by the email-row type
// AND applied to any Module dropped from the Modules tab, so a module behaves like a pre-built row.
const EMAIL_ROW_TOOLBAR = [
  {
    attributes: { class: 'icon icon-add', title: 'Add below' },
    command: 'email:add-below',
  },
  {
    attributes: { class: 'icon icon-move-selection-up', title: 'Move up' },
    command: 'email:row-up',
  },
  {
    attributes: { class: 'icon icon-move-selection-down', title: 'Move down' },
    command: 'email:row-down',
  },
  {
    attributes: { class: 'icon icon-content-copy', title: 'Duplicate' },
    command: 'tlb-clone',
  },
  {
    attributes: { class: 'icon icon-delete', title: 'Delete' },
    command: 'tlb-delete',
  },
];

// A component is "column child" if it sits directly in the single content column (root-cell). Both
// pre-built rows and auto-locked modules qualify → the reorder/add commands work for both.
const isColumnChild = (c) => {
  const p = c && c.parent && c.parent();
  return !!(
    p &&
    p.getAttributes &&
    p.getAttributes()['data-type'] === 'root-cell'
  );
};

// The single content column that holds the rows (created in Grapes.js handleClearCanvas).
const getRootCell = (editor) => {
  const found = editor.getWrapper().find('[data-type="root-cell"]');
  if (found && found.length) return found[0];
  // Fallback: walk the tree for the cell whose data-type attribute is root-cell.
  let match;
  const walk = (comp) =>
    comp.components().each((child) => {
      if (match) return;
      if (child.getAttributes()['data-type'] === 'root-cell') match = child;
      else walk(child);
    });
  walk(editor.getWrapper());
  return match;
};

// Open the row picker and insert the chosen row into the root cell. `config` carries `baseUrl`
// (needed to build the Social row's icon URLs); `at` = insertion index (undefined → append to end).
const openRowPicker = (editor, config, at) => {
  const cell = getRootCell(editor);
  if (!cell) return;

  const wrap = document.createElement('div');
  wrap.className = 'email-row-picker';
  wrap.style.cssText =
    'display:grid;grid-template-columns:repeat(2,1fr);gap:8px;padding:4px;';

  // Sections (pre-built) first, then the basic single-purpose rows.
  [...buildEmailSections(config), ...buildEmailRows(config)].forEach((r) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'email-row-picker__item';
    btn.style.cssText =
      'display:flex;align-items:center;gap:10px;padding:12px;border:1px solid rgba(0,0,0,0.12);' +
      'border-radius:6px;background:#fff;cursor:pointer;font:14px/1.2 Arial,sans-serif;color:#333;text-align:left;';
    btn.innerHTML =
      `<span style="width:22px;height:22px;flex:0 0 22px;color:#d983a6;">${r.media}</span>` +
      `<span>${r.label}</span>`;
    btn.addEventListener('click', () => {
      const added = cell.append(
        r.content,
        typeof at === 'number' ? { at } : {},
      );
      const comp = Array.isArray(added) ? added[0] : added;
      if (comp) editor.select(comp);
      editor.Modal.close();
    });
    wrap.appendChild(btn);
  });

  editor.Modal.open({ title: 'Add content', content: wrap });
};

// Move the selected row among its siblings. GrapesJS move() within the same parent uses `at` in the
// pre-removal indexing, so moving DOWN needs +2 (leapfrog the next sibling after the row is removed).
const moveRow = (editor, dir) => {
  const c = editor.getSelected();
  if (!c || !isColumnChild(c)) return;
  const parent = c.parent();
  if (!parent) return;
  const i = c.index();
  const total = parent.components().length;
  const j = i + dir;
  if (j < 0 || j >= total) return; // already at an edge
  c.move(parent, { at: dir < 0 ? i - 1 : i + 2 });
  editor.select(c); // keep the row selected so the toolbar stays put
};

// GrapesJS flags that make a structural (layout) or interactive element inert to a beginner.
const STRUCT_LOCK = {
  selectable: false,
  hoverable: false,
  editable: false,
  droppable: false,
  draggable: false,
  copyable: false,
  removable: false,
};

// Edit-in-place flags for a content leaf: the beginner can select/edit it (double-click text, click
// an image to replace it) but never move, duplicate or delete it — only whole ROWS move.
const CONTENT_EDIT = {
  selectable: true,
  hoverable: true,
  editable: true,
  draggable: false,
  droppable: false,
  copyable: false,
  removable: false,
};

// A "content leaf" is matched by GrapesJS component TYPE or tag name — so a module built from any
// element set (ul/li, a custom social/menu/video/icon wrapper, …) still gets its structure locked
// and only its real text/image leaves left editable, instead of an allow-list of tags that silently
// misses everything else (a beginner-protocol leak).
const CONTENT_TYPES = new Set(['text', 'link', 'button', 'image']);
const CONTENT_TAGS = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'span',
  'a',
  'img',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'small',
  'label',
]);
const isContentLeaf = (el) => {
  const type = el.get && el.get('type');
  const tag = ((el.get && el.get('tagName')) || '').toLowerCase();
  return CONTENT_TYPES.has(type) || CONTENT_TAGS.has(tag);
};

// Visit every descendant component at any depth — by tree traversal, not a CSS selector, so it
// doesn't depend on which tags/selectors GrapesJS `find` happens to support.
const forEachDescendant = (model, fn) => {
  const kids = model.components && model.components();
  if (!kids || !kids.forEach) return;
  kids.forEach((child) => {
    fn(child);
    forEachDescendant(child, fn);
  });
};

// Transform ONE top-level block into a beginner-safe, edit-in-place, column-locked "row": lock every
// structural/interactive descendant, keep only real content leaves editable (but not movable), and
// make the block itself the movable column-locked unit with the row toolbar. Returns true if it
// transformed (pre-built rows/sections — type 'email-row' — already conform and are skipped).
const lockAsRow = (model) => {
  if (!model || typeof model.get !== 'function') return false;
  if (model.get('type') === EMAIL_ROW) return false; // already a locked row/section
  if (!isColumnChild(model)) return false; // only top-level blocks in the single column

  // Lock everything, re-enabling edit-in-place only on content leaves. Locking by traversal (not a
  // tag allow-list) means a module made of any element/type can't leave selectable structure behind.
  forEachDescendant(model, (el) => {
    el.set(isContentLeaf(el) ? CONTENT_EDIT : STRUCT_LOCK);
  });
  // Make the block itself the movable, column-locked unit with the row toolbar.
  model.set({
    name: 'Section',
    draggable: 'td[data-type="root-cell"]',
    droppable: false,
    copyable: true,
    removable: true,
    toolbar: EMAIL_ROW_TOOLBAR,
  });
  return true;
};

// A freshly-dropped Module (from block:drag:stop) → beginner-safe row, then select it. If it landed
// anywhere other than the single content column (nested in another row, at wrapper level, a path that
// isn't a column child), relocate it into the column FIRST so a beginner can never be left with a
// raw, unlocked block exposing its inner table/td/divs (protocol leak).
const applyModuleProtocol = (editor, model) => {
  if (!model || typeof model.get !== 'function') return;
  if (model.get('type') === EMAIL_ROW) return; // pre-built row/section already conforms
  if (!isColumnChild(model)) {
    const cell = getRootCell(editor);
    if (cell && typeof model.move === 'function') {
      model.move(cell, { at: cell.components().length });
    }
  }
  if (lockAsRow(model)) editor.select(model);
};

// Convert an EXISTING (non-Email, e.g. 700px) template for beginner editing when it is opened in
// Email mode: shrink the body to 600px and lock every top-level block into the beginner protocol so
// the whole template becomes drop-and-edit safe. Best-effort — a legacy template must still open even
// if conversion can't fully apply.
export const convertTemplateForEmail = (editor) => {
  try {
    const roots = editor.getWrapper().find('[data-type="root"]');
    if (roots && roots[0]) {
      // Outlook ignores max-width; pin HTML width + style width (ALW-104994).
      applyOutlookSafePageWidth(roots[0], 600);
    }
    const cell = getRootCell(editor);
    if (cell) cell.components().forEach((child) => lockAsRow(child));
  } catch (e) {
    // conversion is best-effort; never block a legacy template from opening.
  }
};

export default (editor, config = {}) => {
  const domc = editor.DomComponents;
  const cmd = editor.Commands;

  cmd.add('email:row-up', { run: (ed) => moveRow(ed, -1) });
  cmd.add('email:row-down', { run: (ed) => moveRow(ed, 1) });
  // Persistent panel "+ Add content": insert before the LAST row so new content lands above the
  // trailing Footer/unsubscribe row (which every email ends with) rather than below it. Appends when
  // the canvas is empty. (Positional inserts between specific rows use email:add-below.)
  cmd.add('email:add-row', {
    run: (ed) => {
      const cell = getRootCell(ed);
      const n = cell ? cell.components().length : 0;
      openRowPicker(ed, config, n > 0 ? n - 1 : undefined);
    },
  });
  // Insert a new row directly below the selected one (used by the row toolbar "+").
  cmd.add('email:add-below', {
    run: (ed) => {
      const c = ed.getSelected();
      const at = c && isColumnChild(c) ? c.index() + 1 : undefined;
      openRowPicker(ed, config, at);
    },
  });

  domc.addType(EMAIL_ROW, {
    // `data-gjs-type="email-row"` in the block markup already assigns the type; this is a fallback.
    isComponent(el) {
      if (
        el.tagName === 'TABLE' &&
        el.getAttribute('data-gjs-type') === EMAIL_ROW
      ) {
        return { type: EMAIL_ROW };
      }
    },
    model: {
      defaults: {
        name: 'Row',
        // Rows can be dragged to reorder, but ONLY into the single content column — the `draggable`
        // selector constrains valid drop targets to `td[data-type="root-cell"]` (the same mechanism
        // the existing builder uses; it matches the persistent data-type attribute, unlike a data-gjs-*
        // whitelist which GrapesJS strips). So a beginner can't nest a row or drop it anywhere odd;
        // the insertion line only ever appears between rows in the column. See the placement review.
        draggable: 'td[data-type="root-cell"]',
        droppable: false, // content lives in fixed slots; a row is not a drop target
        copyable: true,
        removable: true,
        // toolbar (top-right on hover/select): add-below · up · down · duplicate · delete
        toolbar: EMAIL_ROW_TOOLBAR,
      },
    },
  });

  // Auto-lock a dropped Module into the beginner protocol: reuse the whole Modules library, but make
  // each dropped module behave like a pre-built Section — locked structure, edit-in-place content,
  // column-locked placement, and the row toolbar. Runs only in Email mode (this module loads there only).
  editor.on('block:drag:stop', (model) => applyModuleProtocol(editor, model));
};
